export function getItemTitle(
  id: string,
  data: Elvita.WizardItem[],
  parentId: string | undefined,
): string {
  if (data) {
    const topLevelId = parentId ?? id;
    const topLevel = data.find(item => item.id === topLevelId);

    if (!parentId && topLevel) {
      return topLevel?.title;
    }

    return topLevel?.children?.find(child => child.id === id)?.title ?? '';
  }

  return '';
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export function isWizard(item: any): item is Elvita.WizardItem {
  return item.id !== undefined;
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export function isProduct(item: any): item is Elvita.Product {
  return item.validity !== undefined;
}

/**
 * Resolve a product leaf from the wizard tree by the URL product id
 * (`{externalNo}_{lang}`, e.g. "CKF1142V_en-GB").
 *
 * This is the wizard-based alternative to the productconfigs.json lookup:
 * the leaf already carries `validity`, and manifestId == `{validity}_{lang}`,
 * so the manifest can be resolved without reading productconfigs at all.
 * Several leaves may share the same `validity` (variants pointing at one
 * shared manifest) — each resolves to the same manifest.
 */
/**
 * Client-side product search over the wizard tree — the wizard replaces the
 * productconfigs-derived search index on the render path. Matching is a
 * case-insensitive substring test against the leaf's productId and title
 * (the title carries the product-type wording, e.g. "Kylskåp CKF1142V").
 *
 * Each matching leaf is emitted as its own hit. Variants sharing a manual
 * therefore appear as separate entries, each with its own productId URL —
 * they all resolve to the shared manifest via `findProduct`.
 */
export function searchProducts(
  items: Elvita.WizardItem[],
  term: string,
  lang: string,
): Elvita.ProductSearchDocument[] {
  const needle = term.toLowerCase();
  const results: Elvita.ProductSearchDocument[] = [];

  const collect = (nodes: Elvita.WizardItem[]): void => {
    for (const item of nodes) {
      if (
        isProduct(item) &&
        (item.productId.toLowerCase().includes(needle) ||
          item.title.toLowerCase().includes(needle))
      ) {
        results.push({
          objectId: `${item.productId}_${lang}`,
          manifestId: `${item.validity}_${lang}`,
          imageId: item.imageId,
          description: item.title,
          languageCode: lang,
        });
      }
      if (item.children) {
        collect(item.children);
      }
    }
  };

  collect(items);
  return results;
}

export function findProduct(
  items: Elvita.WizardItem[],
  productId: string,
  lang: string,
): Elvita.Product | undefined {
  for (const item of items) {
    if (isProduct(item) && `${item.productId}_${lang}` === productId) {
      return item;
    }
    if (item.children) {
      const found = findProduct(item.children, productId, lang);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Collect ALL product leaves carrying the given validity — the wizard entry
 * points of one shared manifest (e.g. variant models sharing one manual).
 */
export function findProductsByValidity(
  items: Elvita.WizardItem[],
  validity: number,
): Elvita.Product[] {
  const found: Elvita.Product[] = [];
  for (const item of items) {
    if (isProduct(item) && item.validity === validity) {
      found.push(item);
    }
    if (item.children) {
      found.push(...findProductsByValidity(item.children, validity));
    }
  }
  return found;
}

/**
 * Find any product leaf carrying the given validity — used to resolve
 * manifest-style URLs (`/{validity}_{lang}`), where the URL itself is the
 * shared manifest id and any leaf with that validity confirms it exists.
 */
export function findProductByValidity(
  items: Elvita.WizardItem[],
  validity: number,
): Elvita.Product | undefined {
  return findProductsByValidity(items, validity)[0];
}

/** Model codes named inline before the label falls back to a count. */
const INLINE_CODE_LIMIT = 5;

export function isProductGroup(
  item: Elvita.ProductListEntry | Elvita.ProductGroup,
): item is Elvita.ProductGroup {
  return 'base' in item;
}
function findBase(members: Elvita.Product[]): Elvita.Product | undefined {
  const prefixes = members.filter(candidate =>
    members.every(
      other =>
        other === candidate || other.productId.startsWith(candidate.productId),
    ),
  );

  return prefixes.length === 1 ? prefixes[0] : undefined;
}

export function orderByBase(products: Elvita.Product[]): Elvita.Product[] {
  const base = findBase(products);

  if (!base) return products;

  return [base, ...products.filter(product => product !== base)];
}

export function groupProductsByValidity(
  items: Elvita.ProductListEntry[],
): (Elvita.ProductListEntry | Elvita.ProductGroup)[] {
  const membersByValidity = new Map<number, Elvita.Product[]>();

  for (const item of items) {
    if (!isProduct(item)) continue;

    const members = membersByValidity.get(item.validity);
    if (members) {
      members.push(item);
    } else {
      membersByValidity.set(item.validity, [item]);
    }
  }

  const emitted = new Set<number>();
  const result: (Elvita.ProductListEntry | Elvita.ProductGroup)[] = [];

  for (const item of items) {
    if (!isProduct(item)) {
      result.push(item);
      continue;
    }

    const members = membersByValidity.get(item.validity) ?? [item];

    if (members.length < 2) {
      result.push(item);
      continue;
    }

    if (emitted.has(item.validity)) continue;
    emitted.add(item.validity);

    const base = findBase(members);

    if (!base) {
      result.push(...members);
      continue;
    }

    result.push({
      base,
      variants: members.filter(member => member !== base),
    });
  }

  return result;
}

export function formatGroupLabel(
  group: Elvita.ProductGroup,
  t: (key: string, fallback?: string) => string,
): string {
  const codes = [group.base, ...group.variants].map(
    product => product.productId,
  );

  if (codes.length <= INLINE_CODE_LIMIT) {
    return codes.join(' / ');
  }

  const suffix = t('wizard.variants.more', '(+{count} versions)').replace(
    '{count}',
    group.variants.length.toString(),
  );

  return `${group.base.productId} ${suffix}`;
}
